/**
 * useFlow - Hook for the unified flow engine
 *
 * Works with the unified chat endpoint where LLM drives with tools.
 * Handles sending messages, receiving responses, and flow handoffs.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import type {
  FlowResponse,
  FlowErrorResponse,
  FlowRequest,
  Layout,
  Action,
} from "@um/contracts";

interface UseFlowOptions {
  flowType: string;
  accountId: string;
  // Optional character context for character-chat flows
  animalId?: string;
  personaId?: string;
  onComplete?: (data: Record<string, unknown>) => void;
}

interface FlowState {
  currentFlowType: string;
  sessionId: string | null;
  message: string | null;
  imageUrl: string | null;
  layout: Layout;
  inputRequired: boolean;
  action: Action | null; // Action component for two-section layout
  collectedData: Record<string, unknown>;
  isComplete: boolean;
  handoffTo: string | null;
  isLoading: boolean;
  toolInProgress: string | null; // Tool currently executing (e.g., "save" during image generation)
  error: string | null;
}

// Use the unified chat endpoint
const callFlow = httpsCallable<FlowRequest, FlowResponse | FlowErrorResponse>(
  functions,
  "chat"
);

// Get storage key for session persistence
const getSessionKey = (flowType: string, accountId: string) =>
  `flow_session_${flowType}_${accountId}`;

// Get storage key for active flow (tracks which flow we're currently in after handoffs)
const getActiveFlowKey = (initialFlowType: string, accountId: string) =>
  `flow_active_${initialFlowType}_${accountId}`;

export function useFlow({
  flowType: initialFlowType,
  accountId,
  animalId,
  personaId,
  onComplete,
}: UseFlowOptions) {
  // Check if we have an active flow from a previous handoff
  const activeFlow = accountId
    ? localStorage.getItem(getActiveFlowKey(initialFlowType, accountId)) ||
      initialFlowType
    : initialFlowType;

  // Load sessionId for the active flow
  const storedSessionId = accountId
    ? localStorage.getItem(getSessionKey(activeFlow, accountId))
    : null;

  const [state, setState] = useState<FlowState>({
    currentFlowType: activeFlow,
    sessionId: storedSessionId,
    message: null,
    imageUrl: null,
    layout: "full",
    inputRequired: true,
    action: null,
    collectedData: {},
    isComplete: false,
    handoffTo: null,
    isLoading: false,
    toolInProgress: null,
    error: null,
  });

  // Use refs to avoid recreating callbacks
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Persist sessionId to localStorage when it changes
  const saveSessionId = useCallback(
    (sessionId: string | null, flowTypeOverride?: string) => {
      const key = getSessionKey(
        flowTypeOverride || state.currentFlowType,
        accountId
      );
      if (sessionId) {
        localStorage.setItem(key, sessionId);
      } else {
        localStorage.removeItem(key);
      }
    },
    [state.currentFlowType, accountId]
  );

  const handleResponse = useCallback(
    (response: FlowResponse | FlowErrorResponse) => {
      if (response.success) {
        // Persist sessionId to localStorage
        saveSessionId(response.sessionId);

        setState((prev) => ({
          ...prev,
          sessionId: response.sessionId,
          message: response.message,
          imageUrl: response.imageUrl || prev.imageUrl,
          layout: response.layout || "full",
          inputRequired: response.inputRequired !== false,
          action: response.action || null,
          collectedData: response.collectedData || prev.collectedData,
          isComplete: response.isComplete || false,
          handoffTo: response.handoffTo || null,
          isLoading: false,
          error: null,
        }));
      } else {
        const failure = response as FlowErrorResponse;
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: failure.error.message || "Unknown error",
        }));
      }
    },
    [saveSessionId]
  );

  // Handle handoffs automatically
  useEffect(() => {
    if (state.handoffTo && !state.isLoading) {
      switchFlow(state.handoffTo);
    }
  }, [state.handoffTo]);

  const start = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    // Check for existing session in localStorage
    const existingSessionId = localStorage.getItem(
      getSessionKey(state.currentFlowType, accountId)
    );

    try {
      const request: FlowRequest = {
        flowType: state.currentFlowType,
        action: existingSessionId ? "respond" : "start",
        accountId,
        ...(animalId && { animalId }),
        ...(personaId && { personaId }),
      };
      if (existingSessionId) {
        request.sessionId = existingSessionId;
      }

      const result = await callFlow(request);
      handleResponse(result.data);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to start flow",
      }));
    }
  }, [state.currentFlowType, accountId, animalId, personaId, handleResponse]);

  const sendMessage = useCallback(
    async (message: string, imageBase64?: string) => {
      if (!state.sessionId) return;

      setState((prev) => ({ ...prev, isLoading: true, toolInProgress: null, error: null }));

      try {
        const result = await callFlow({
          flowType: state.currentFlowType,
          sessionId: state.sessionId,
          message,
          // Only include imageBase64 if it has a value (not undefined/null)
          ...(imageBase64 ? { imageBase64 } : {}),
          action: "respond",
          accountId,
        });
        
        // Clear toolInProgress on final response
        setState(prev => ({ ...prev, toolInProgress: null }));
        handleResponse(result.data);
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          toolInProgress: null,
          error:
            error instanceof Error ? error.message : "Failed to send message",
        }));
      }
    },
    [state.currentFlowType, accountId, state.sessionId, handleResponse]
  );

  // Call this when user clicks "Done" on the final step
  const complete = useCallback(() => {
    // Clear session from localStorage
    saveSessionId(null);
    // Clear active flow tracking
    localStorage.removeItem(getActiveFlowKey(initialFlowType, accountId));
    // Call onComplete callback
    if (onCompleteRef.current) {
      onCompleteRef.current(state.collectedData);
    }
  }, [saveSessionId, state.collectedData, initialFlowType, accountId]);

  // Switch to a different flow (for handoffs)
  const switchFlow = useCallback(
    async (newFlowType: string) => {
      setState((prev) => ({
        ...prev,
        currentFlowType: newFlowType,
        sessionId: null,
        action: null,
        handoffTo: null,
        isLoading: true,
        error: null,
      }));

      // Persist which flow we're now in (for page refresh)
      localStorage.setItem(
        getActiveFlowKey(initialFlowType, accountId),
        newFlowType
      );

      try {
        const result = await callFlow({
          flowType: newFlowType,
          action: "start",
          accountId,
        });

        const data = result.data;
        if (data.success) {
          // Save new session to localStorage with the new flow type
          saveSessionId(data.sessionId, newFlowType);

          setState((prev) => ({
            ...prev,
            currentFlowType: newFlowType,
            sessionId: data.sessionId,
            message: data.message,
            imageUrl: data.imageUrl || null,
            layout: data.layout || "full",
            inputRequired: data.inputRequired !== false,
            action: data.action || null,
            collectedData: data.collectedData || {},
            isComplete: data.isComplete || false,
            handoffTo: data.handoffTo || null,
            isLoading: false,
            error: null,
          }));
        } else {
          const failure = data as FlowErrorResponse;
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: failure.error?.message || "Unknown error",
          }));
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error:
            error instanceof Error ? error.message : "Failed to switch flow",
        }));
      }
    },
    [accountId, saveSessionId, initialFlowType]
  );

  const reset = useCallback(async () => {
    // Reset back to initial flow type
    setState((prev) => ({
      ...prev,
      currentFlowType: initialFlowType,
      message: null,
      imageUrl: null,
      action: null,
      collectedData: {},
      isComplete: false,
      handoffTo: null,
      isLoading: true,
      error: null,
    }));

    // Clear active flow tracking
    localStorage.removeItem(getActiveFlowKey(initialFlowType, accountId));

    // Clear session for current flow (if different from initial)
    if (state.currentFlowType !== initialFlowType) {
      saveSessionId(null, state.currentFlowType);
    }

    // Get current sessionId from localStorage (might be different from state)
    const currentSessionId = localStorage.getItem(
      getSessionKey(initialFlowType, accountId)
    );

    // Clear localStorage for initial flow
    saveSessionId(null, initialFlowType);

    try {
      const request: FlowRequest = {
        flowType: initialFlowType,
        action: "reset",
        accountId,
        ...(animalId && { animalId }),
        ...(personaId && { personaId }),
      };
      if (currentSessionId) {
        request.sessionId = currentSessionId;
      }

      const result = await callFlow(request);
      handleResponse(result.data);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Failed to reset flow",
      }));
    }
  }, [
    initialFlowType,
    accountId,
    animalId,
    personaId,
    saveSessionId,
    handleResponse,
    state.currentFlowType,
  ]);

  return {
    ...state,
    start,
    sendMessage,
    reset,
    complete,
  };
}
